import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Facebook, Instagram, Youtube } from "lucide-react";
import { company, navLinks } from "../mock";
import { useToast } from "../hooks/use-toast";
import { subscribeEmail } from "../api";

const Footer = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribeEmail(email);
    } catch (err) {
      // Non-blocking
    }
    toast({ title: "Subscribed!", description: "Thanks for joining our newsletter." });
    setEmail("");
  };

  return (
    <footer className="bg-brand-navy text-white/80">
      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-5">
            <img
              src={company.logo}
              alt="Fab Build Pro logo"
              className="h-16 w-auto object-contain"
            />
          </div>
          <p className="text-sm leading-relaxed text-white/65">
            A trusted full-service roofing and construction company proudly
            serving Northern Virginia and the D.C. Metro area. Built on
            integrity, transparency, and craftsmanship.
          </p>
          <div className="flex gap-3 mt-5">
            <a href={company.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand-orange flex items-center justify-center transition-colors">
              <Facebook className="w-4 h-4 text-white" />
            </a>
            <a href={company.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand-orange flex items-center justify-center transition-colors">
              <Instagram className="w-4 h-4 text-white" />
            </a>
            <a href={company.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand-orange flex items-center justify-center transition-colors">
              <Youtube className="w-4 h-4 text-white" />
            </a>
          </div>
          <div className="mt-5">
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Find & Review Us</p>
            <div className="flex flex-wrap gap-2">
              {company.profiles.map((p) => (
                <a
                  key={p.label}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs bg-white/10 hover:bg-brand-orange text-white/90 hover:text-white px-3 py-1.5 rounded-full transition-colors"
                >
                  {p.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4">Quick Links</h4>
          <ul className="space-y-2.5 text-sm">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-brand-orange transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4">Contact Us</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={company.phoneHref} className="flex items-center gap-2.5 hover:text-brand-orange transition-colors">
                <Phone className="w-4 h-4 text-brand-orange" /> {company.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${company.email}`} className="flex items-center gap-2.5 hover:text-brand-orange transition-colors">
                <Mail className="w-4 h-4 text-brand-orange" /> {company.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-brand-orange mt-0.5" />
              Northern Virginia · Washington D.C. · Maryland
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4">Newsletter</h4>
          <p className="text-sm text-white/65 mb-4">
            Roofing tips, seasonal specials, and home care advice.
          </p>
          <form onSubmit={subscribe} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-brand-orange"
            />
            <button
              type="submit"
              className="bg-brand-orange hover:bg-brand-orangedark rounded-xl px-3 flex items-center justify-center transition-colors"
              aria-label="Subscribe"
            >
              <ArrowRight className="w-5 h-5 text-white" />
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-3 text-center">Popular Roofing Services</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { label: "Storm Damage Roof Repair", to: "/storm-damage-roof-repair-virginia" },
              { label: "Wind Damage Roof Repair", to: "/wind-damage-roof-repair" },
              { label: "Roofing Contractor McLean VA", to: "/roofing-contractor-mclean-va" },
              { label: "Roofing Contractor Vienna VA", to: "/roofing-contractor-vienna-va" },
              { label: "Roof Maintenance", to: "/roof-maintenance" },
              { label: "Roof Repair", to: "/services/roof-repair" },
              { label: "Roof Replacement", to: "/services/roof-replacement" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-xs bg-white/5 hover:bg-brand-orange text-white/80 hover:text-white px-3 py-1.5 rounded-full transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/50">
          <p>© {new Date().getFullYear()} Fab Build Pro. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/contact" className="hover:text-brand-orange transition-colors">Terms of Service</Link>
            <Link to="/privacy-policy" className="hover:text-brand-orange transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
